import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIdentity } from '@gov-nx/auth/common/hooks/useIdentity';
import { useSessionStoredUrl } from '@gov-nx/utils/web/hooks/useSessionStoredUrl';
import { pageRouteResolver } from '@gov-nx/web/Router/routeResolver';

const AuthorizedRoute = ({ children }: { children: ReactNode }) => {
	const { isAuthenticated } = useIdentity();
	const location = useLocation();
	const { pageAfterLogin, queryParamsAfterLogin } = useSessionStoredUrl();

	/**
	 * NIA hands the authorization result back to this route (`/autorizace` is an
	 * nginx-owned endpoint that 302s here with `Id` / `Result`). The result must
	 * always reach the page below — it is the only place that closes the
	 * authorization on the BE (`dokoncit`) and reports the outcome to the user.
	 *
	 * A stored post-login target must therefore never be restored on this route.
	 * Such a target outlives the flow that wrote it (a 401 anywhere in the app
	 * runs `rememberCurrentLocation`, and an interrupted login leaves its own
	 * leftovers), and the restore below would then replace the NIA return URL —
	 * dropping the `Id` / `Result` params before the page mounts. The
	 * authorization would silently stay open on the BE with no error, no message
	 * and no way to recover it.
	 */
	const isAuthorizationResult =
		location.pathname === pageRouteResolver('autorizace.vysledek');

	useEffect(() => {
		if (queryParamsAfterLogin.has('formId')) {
			pageAfterLogin.set(pageRouteResolver('dashboard'));
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryParamsAfterLogin.value]);

	useEffect(() => {
		if (isAuthenticated && pageAfterLogin.equalsTo(location.pathname)) {
			pageAfterLogin.remove();
		}
	}, [pageAfterLogin.value, location, isAuthenticated, pageAfterLogin]);

	useEffect(() => {
		if (isAuthenticated && queryParamsAfterLogin.equalsTo(location.search)) {
			queryParamsAfterLogin.remove();
		}
	}, [
		queryParamsAfterLogin.value,
		location,
		isAuthenticated,
		queryParamsAfterLogin,
	]);

	if (!isAuthenticated) {
		if (location.search) {
			queryParamsAfterLogin.set(location.search);
		}
		if (location.pathname !== '/') {
			pageAfterLogin.set(location.pathname);
		}

		return (
			<Navigate
				to={pageRouteResolver('prihlaseni')}
				replace
			/>
		);
	}

	// Authenticated from here on. Consume any stored target instead of restoring
	// it, so it can hijack neither this route nor the return URL the page
	// navigates to once the result has been processed.
	if (isAuthorizationResult) {
		pageAfterLogin.remove();
		queryParamsAfterLogin.remove();

		return children;
	}

	if (
		isAuthenticated &&
		(pageAfterLogin.value || queryParamsAfterLogin.value) &&
		!(
			pageAfterLogin.equalsTo(location.pathname) ||
			queryParamsAfterLogin.equalsTo(location.search)
		)
	) {
		return (
			<Navigate
				to={{
					pathname: pageAfterLogin.value ?? '/',
					search: queryParamsAfterLogin.value ?? '',
				}}
				replace
			/>
		);
	}

	return children;
};

export default AuthorizedRoute;
